import React from 'react';
import PropTypes from 'prop-types';
import capitalize from 'capitalize';
import _ from 'lodash';
import { withRouter } from 'react-router';
import moment from 'moment';
import { Card, Label, Divider, Icon, Grid, Dropdown, Popup, Table, Button, Flag } from 'semantic-ui-react';
import CAMPAIGN_TYPES from '../../utils/campaign-types';

import { renderCountryName } from '../../utils/helpers/countries';

const fontSize13 = {
  fontSize: 13,
};

const labelStyle = {
  width: '100%',
  textAlign: 'center',
  marginTop: '3px',
};

const STYLES = {
  fontSizeLarge: {
    fontSize: '100%',
  },
  fontSizeSmall: {
    fontSize: '90%',
  },
  empty: {},
  noPaddingBottom: {
    padding: 10,
  },
  noPadding: {
    padding: 0,
  },
  compactVertical: {
    padding: '5px 14px',
  },
  noVerticalPadding: {
    padding: '0px 14px',
  },
  displayBlock: {
    display: 'block',
  },
  fontGreen: {
    color: '#20c49e',
    fontSize: '90%',
    lineHeight: '12px',
  },
  fontGrey: {
    color: 'grey',
    fontSize: '90%',
    lineHeight: '12px',
  },
};

const getFriendlyRole = role => capitalize.words(_.toLower(_.replace(role, '_', ' ')));

const smallText = {
  fontSize: '90%',
  lineHeight: '12px',
};

const handleClick = ({ router, campaignType, campaignId, canLoadFast }) => {
  const isACR = campaignType === 'ACR';

  const campUrl = `/campaign/${campaignId}/collaborations${canLoadFast ? '?fast=true' : ''}`;
  const campaignUrl = isACR ? `/acr-campaign/${campaignId}` : campUrl;
  router.push(campaignUrl);
};

const tagLabelStyle = {
  cursor: 'pointer',
  width: 'min-content',
  display: 'block',
};

const CampaignCard = props => {
  const {
    campaignId,
    campaignPermissionId,
    campaignName,
    campaignType,
    campaignCountry,
    campaignCreatedDate,
    campaignCreatedBy,
    influencersCount,
    totalAssetsCount,
    teamMemberCount,
    marketingCampaignsCount,
    campaignBaseTrackingUrl,
    campaignBrand,
    campaignWorkflow,
    campaignCanLoadFast,
    router,
    listType,
    onDelete,
    onArchive,
    onUnarchive,
    isUserSuperAdmin,
  } = props;
  const isActiveList = listType === 'ACTIVE';
  const friendlyDate = moment(campaignCreatedDate).format('Do MMM');
  const hasAssets = totalAssetsCount > 0;
  const clf = influencersCount > 60 && campaignCanLoadFast;
  const onClickListener = () => handleClick({ router, campaignType, campaignId, canLoadFast: clf });
  const color = CAMPAIGN_TYPES[campaignType].color;
  const hasMCs = marketingCampaignsCount ? marketingCampaignsCount > 0 : false;
  const hasGAIntegrated = !_.isEmpty(campaignBaseTrackingUrl);
  const integrationsCount = _.add(marketingCampaignsCount || 0, hasGAIntegrated ? 1 : 0);
  const hasCountry = !_.isNil(campaignCountry);

  const getWorkflow = workflow => {
    if (workflow === 'ABBLY_V1') return 'UK (global) Store';
    if (workflow === 'ABBLY_V1_US') return 'US Store';
    return workflow;
  };
  return (
    <Card onClick={onClickListener} color={color}>
      <Card.Content>
        <Card.Header>
          <Grid>
            <Grid.Row>
              <Grid.Column width={13}>
                { campaignName }
              </Grid.Column>
              <Grid.Column width={3}>
                <Dropdown
                  icon={null}
                  pointing="top right"
                  floating
                  trigger={<Button icon="bars" color="pink" inverted size="small" />}
                >
                  {
                    isActiveList ?
                      <Dropdown.Menu className="left">
                        <Dropdown.Item
                          disabled={isUserSuperAdmin}
                          onClick={() => onArchive(campaignPermissionId)}
                        >Archive</Dropdown.Item>
                      </Dropdown.Menu>
                      :
                      <Dropdown.Menu className="left">
                        <Dropdown.Item onClick={() => onUnarchive(campaignPermissionId)} >Unarchive</Dropdown.Item>
                        <Dropdown.Item onClick={() => onDelete(campaignPermissionId)}>Delete</Dropdown.Item>
                      </Dropdown.Menu>
                  }
                </Dropdown>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Card.Header>
        <Card.Description>
          <Divider />
          <Table basic="very">
            {
              campaignBrand &&
              <Table.Row>
                <Label style={labelStyle} color="grey" basic content={campaignBrand} />
              </Table.Row>
            }
            {
              campaignWorkflow &&
              <Table.Row>
                <Label style={labelStyle} color="blue" basic>

                Workflow
                  <Label.Detail>
                    {_.toUpper(getWorkflow(campaignWorkflow))}
                  </Label.Detail>
                </Label>
              </Table.Row>
            }

            <Table.Row>
              <Table.Cell width={hasCountry ? '14' : '16'}>
                <Label style={labelStyle} color={color} basic content={CAMPAIGN_TYPES[campaignType].name} />
              </Table.Cell>
              {
                hasCountry &&
                <Table.Cell width="2" textAlign="center">
                  <Popup
                    trigger={
                      <Flag
                        name={_.lowerCase(campaignCountry)}
                      />
                    }
                    content={renderCountryName(campaignCountry)}
                  />
                </Table.Cell>
              }
            </Table.Row>
          </Table>
        </Card.Description>
      </Card.Content>
      <Card.Content>
        <Card.Meta>
          Created by {campaignCreatedBy} on {friendlyDate}
        </Card.Meta>
      </Card.Content>
      <Card.Content style={STYLES.compactVertical}>
        <Table basic="very" singleLine fixed>
          <Table.Body>
            <Table.Row style={smallText}>
              <Table.Cell style={{ padding: 0, paddingRight: '5px' }}>
                <Popup
                  trigger={
                    <Label basic style={STYLES.displayBlock}>
                      <Icon name="user outline" color="pink" /> {influencersCount}
                    </Label>
                  }
                  content="Number of influencers added to this campaign"
                />
              </Table.Cell>
              <Table.Cell style={{ padding: 0, paddingRight: '5px' }}>
                <Popup
                  trigger={
                    <Label basic style={STYLES.displayBlock}>
                      <Icon color="green" name="file outline" /> { totalAssetsCount }
                    </Label>
                  }
                  content="Number of tracked assets on this campaign"
                />
              </Table.Cell>
              <Table.Cell style={{ padding: 0 }}>
                <Popup
                  trigger={
                    <Popup
                      trigger={
                        <Label basic style={STYLES.displayBlock}>
                          <Icon
                            color={`${teamMemberCount === 1 ? 'orange' : 'green'}`}
                            name="user outline"
                          /> { teamMemberCount }
                        </Label>
                      }
                      content={
                        teamMemberCount === 1 ?
                          'Only you can see this campaign. No-one else has been invited.'
                          :
                          `${teamMemberCount} people can see this campaign.`}
                    />
                  }
                />
              </Table.Cell>
              {
                (hasMCs || hasGAIntegrated) &&
                <Table.Cell>
                  <Popup
                    trigger={
                      <Popup
                        trigger={
                          <Label basic style={STYLES.displayBlock}>
                            <Icon
                              color="blue"
                              name="linkify"
                            /> {integrationsCount}
                          </Label>
                        }
                        content={
                          <div>
                            {
                              hasMCs &&
                              <div style={fontSize13}>
                                <Icon color="blue" name="facebook" />
                                Facebook Ads Campaign
                              </div>
                            }
                            {
                              hasGAIntegrated &&
                              <div style={fontSize13}>
                                <Icon color="red" name="google" />
                                Google Analytics Campaign
                              </div>
                            }
                          </div>
                        }
                      />
                    }
                  />
                </Table.Cell>
              }

            </Table.Row>
          </Table.Body>
        </Table>
      </Card.Content>
    </Card>
  );
};

CampaignCard.propTypes = {
  campaign: PropTypes.object.isRequired,
  campaignPermission: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  listType: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onUnarchive: PropTypes.func.isRequired,
};


export default withRouter(CampaignCard);



/** WEBPACK FOOTER **
 ** ./src/components/campaign-card/index.js
 **/